import { createContext, useContext, useEffect, useState } from 'react';
import { fetchPublicThemeConfig, fetchThemeConfig } from '../apis/md/customization/jsonConfigs';
import { useAuth0 } from '@auth0/auth0-react';
import {
  DEFAULT_PRIVATE_FEATURE_FLAGS,
  DEFAULT_PUBLIC_FEATURE_FLAGS,
  PrivateFeatureFlags,
  PublicFeatureFlags,
  THEME_CONFIG_KEYS,
} from '@lemontaps/lib/constants';
import { useAppSelector } from '@/application/hooks';

export default function useFeatureFlags() {
  const { isLoading: isAuth0Loading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const domainVerified = useAppSelector(state => state.login.domainVerified);

  const [publicFlags, setPublicFlags] = useState<{
    flags: PublicFeatureFlags;
    loaded: boolean;
  }>({
    flags: DEFAULT_PUBLIC_FEATURE_FLAGS,
    loaded: false,
  });
  const [privateFlags, setPrivateFlags] = useState<{
    flags: PrivateFeatureFlags;
    loaded: boolean;
  }>({
    flags: DEFAULT_PRIVATE_FEATURE_FLAGS,
    loaded: false,
  });

  useEffect(() => {
    if (domainVerified) {
      fetchPublicThemeConfig(THEME_CONFIG_KEYS.FEATURE_FLAGS_PUBLIC)
        .then(res =>
          setPublicFlags({
            flags: (res.value || {}) as PublicFeatureFlags,
            loaded: true,
          }),
        )
        .catch(() => setPublicFlags(prev => ({ ...prev, loaded: true })));
    }
  }, [domainVerified]);

  useEffect(() => {
    if (!isAuth0Loading && domainVerified && isAuthenticated) {
      fetchThemeConfig(getAccessTokenSilently, THEME_CONFIG_KEYS.FEATURE_FLAGS_PRIVATE)
        .then(res =>
          setPrivateFlags({
            flags: (res.value || {}) as PrivateFeatureFlags,
            loaded: true,
          }),
        )
        .catch(() => setPrivateFlags(prev => ({ ...prev, loaded: true })));
    } else if (!isAuth0Loading && !isAuthenticated) {
      setPrivateFlags({ flags: {} as PrivateFeatureFlags, loaded: true });
    }
  }, [getAccessTokenSilently, isAuth0Loading, domainVerified, isAuthenticated]);

  return {
    publicFlags: publicFlags.flags,
    privateFlags: privateFlags.flags,
    isLoading: !publicFlags.loaded || !privateFlags.loaded,
  };
}

export const FeatureFlagContext = createContext<{
  publicFlags: PublicFeatureFlags;
  privateFlags: PrivateFeatureFlags;
}>({
  publicFlags: DEFAULT_PUBLIC_FEATURE_FLAGS,
  privateFlags: DEFAULT_PRIVATE_FEATURE_FLAGS,
});

export const usePublicFeatureFlag = (key: keyof PublicFeatureFlags) => {
  const context = useContext(FeatureFlagContext);
  return context.publicFlags[key as keyof PublicFeatureFlags];
};
export const usePrivateFeatureFlag = (key: keyof PrivateFeatureFlags) => {
  const context = useContext(FeatureFlagContext);
  return context.privateFlags[key as keyof PrivateFeatureFlags];
};
